import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { API_BASE } from "../Utils/constant";
import { toast } from "react-toastify";
import { useGetHabits } from "../Controllers/DashboardController";
import { AUTH_TOKEN } from "../constants/localStorageKeys";
import { useQueryClient } from "@tanstack/react-query";
import { retrieve } from "../helpers/StorageHelper";
import {
  invalidateLevelQueries,
  invalidateHabitQueries,
} from "../Utils/queryInvalidation";

const HabitsTracker = ({ rootClassName = "" }) => {
  const [error, setError] = useState(null);
  const today = moment().format("YYYY-MM-DD");
  const [isHabitToggling, setIsHabitToggling] = useState(false);

  const queryClient = useQueryClient();

  // Use the new controller hook
  const {
    data: habitsData,
    isLoading: loading,
    error: habitsError,
    refetch: refetchHabits,
  } = useGetHabits(today, today);

  useEffect(() => {
    if (habitsError) {
      setError("Failed to load habits data");
    }
  }, [habitsError]);

  const getHabitStatus = (habit) => {
    if (!habit || !habit.tracked_data || habit.tracked_data.length === 0)
      return false;

    return habit.tracked_data[0].status === 1;
  };

  const toggleHabitStatus = async (habitId, currentStatus) => {
    setIsHabitToggling(true);
    const token = retrieve(AUTH_TOKEN);
    const newStatus = currentStatus ? 0 : 1;

    try {
      await axios.post(
        `${API_BASE}/user/habit/update-status`,
        {},
        {
          params: {
            habits_id: habitId,
            habit_date: today,
            status: newStatus,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Use centralized query invalidation for level-up detection
      invalidateLevelQueries(queryClient);
      invalidateHabitQueries(queryClient);

      // Refresh data after update using React Query
      refetchHabits();
    } catch (error) {
      console.error("Failed to update habit status", error);
      toast.error("Failed to update habit status");
    } finally {
      setIsHabitToggling(false);
    }
  };

  if (loading)
    return (
      <div
        className={`col-lg-12 col-xl-6 text-center text-white ${rootClassName}`.trim()}
      >
        Loading...
      </div>
    );
  if (error)
    return (
      <div className={`col-lg-12 col-xl-6 ${rootClassName}`.trim()}>
        {error}
      </div>
    );
  if (!habitsData)
    return (
      <div className={`col-lg-12 col-xl-6 text-center ${rootClassName}`.trim()}>
        No habits data available
      </div>
    );

  const categoryOrder = ["body", "mind", "balance", "wealth"];

  const interleaveHabits = () => {
    const categoryOrder = ["body", "mind", "balance", "wealth"];
    const maxHabits = Math.max(
      ...categoryOrder.map((cat) =>
        habitsData[cat] ? Object.keys(habitsData[cat]).length : 0,
      ),
    );

    const result = [];

    for (let i = 0; i < maxHabits; i++) {
      for (const cat of categoryOrder) {
        const habits = habitsData[cat] || {};
        const entries = Object.entries(habits);
        if (entries[i]) {
          const [habitName, habitData] = entries[i];
          result.push({ category: cat, habitName, habitData });
        }
      }
    }

    return result;
  };

  const flattenedHabits = interleaveHabits();

  return (
    <div className={`col-lg-12 col-xl-6 ${rootClassName}`.trim()}>
      <div className="dashboard-habit-cards">
        <h3 className="habits-title">Habits</h3>

        {categoryOrder.some(
          (cat) => habitsData[cat] && Object.keys(habitsData[cat]).length > 0,
        ) ? (
          <div className="desktop-layout">
            <div className="category-headers">
              {categoryOrder.map((cat) => (
                <div key={cat} className="category-label text-body-b1">
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </div>
              ))}
            </div>

            <div className="habits-grid">
              {flattenedHabits.map(({ category, habitName, habitData }) => {
                const isActive = getHabitStatus(habitData);
                const isJournalHabit = habitName === "Journal";
                return (
                  <div
                    key={habitName}
                    className={`habit-item ${isActive ? "active" : ""}`}
                    title={
                      /*
                      isJournalHabit && !isActive
                        ? "Complete AM journal to complete the journal habit."
                        : undefined
                      */
                      undefined
                    }
                    onClick={() => {
                      if (isHabitToggling) return;
                      /* TODO: uncomment this when journal is fully implemented
                      if (isJournalHabit) {
                        if (!isActive) {
                          toast.error(
                            "Complete AM journal to complete the journal habit.",
                          );
                        }
                        return;
                      }
                      */
                      toggleHabitStatus(habitData.id, isActive);
                    }}
                    style={{
                      /* cursor: isJournalHabit ? "not-allowed" : "pointer", */
                      cursor: "pointer",
                      opacity: isHabitToggling ? 0.5 : 1,
                    }}
                  >
                    <img
                      className="habit-icon"
                      src={habitData.logo || "/icons/cloud-lightning.svg"}
                      alt={habitName}
                    />
                    <div
                      className="habit-name habit-name-update"
                      title={habitName}
                    >
                      {habitName.charAt(0).toUpperCase() + habitName.slice(1)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-body-b2 mt-3">No habits available to show.</p>
        )}
      </div>
    </div>
  );
};

export default HabitsTracker;
